import { useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useDashboard,
  useMarkWorkout,
  type DashboardResponse,
  type DashboardStrava,
  type PlannedWorkout,
  type WeekDay,
  type WorkoutStatus,
} from '@/lib/workouts';
import { useStravaSync } from '@/lib/strava';
import {
  DashboardKpisSchema,
  type DashboardKpis,
  type ActivityHeatmapDay,
} from '@/lib/dashboard';
import {
  WorkoutCard,
  disciplineBadgeClass,
  disciplineLabel,
} from './WorkoutCard';
import { WeekNavigator } from './WeekNavigator';
import { RecentActivitiesCard } from './RecentActivitiesCard';
import { HeroStatusBar } from './HeroStatusBar';
import { WeekSummaryCard } from './WeekSummaryCard';
import { FormFitnessChart } from './FormFitnessChart';
import { DisciplineStatsGrid } from './DisciplineStatsGrid';
import { ActivityHeatmap } from './ActivityHeatmap';
import { DaySelectionPanel } from './DaySelectionPanel';
import { DisciplineHoursChart } from './DisciplineHoursChart';
import { AlertsBanner } from './AlertsBanner';
import { MorningBiometricsCard } from './MorningBiometricsCard';

/**
 * Dashboard `/` — rediseño M2.16 (bento grid 12 columnas full-width).
 *
 * Layout (de arriba abajo):
 *   1. AlertsBanner — alertas accionables (si las hay).
 *   2. HeroStatusBar — saludo + CTL/ATL/TSB grandes + botón sync Strava +
 *      chips KPIs derivados (monotonía, strain, ramp, ACWR, compliance,
 *      días sin off, próxima carrera A).
 *   3. FormFitnessChart (col-span-8) + NextWorkoutCard/StravaMiniCard
 *      (col-span-4) — fila bento 12 cols.
 *   4. DisciplineHoursChart — gráfica apilada horas por disciplina con
 *      toggle Semana/Mes.
 *   5. WeekSummaryCard (col-span-6) + DisciplineStatsGrid (col-span-6).
 *   6. ActivityHeatmap clicable.
 *   7. WeekNavigator + grid semanal Lun-Dom + empty state.
 *   8. RecentActivitiesCard.
 *
 * Decisiones:
 *  - Container `space-y-6` sin `max-w` propio. El `max-w` global lo pone
 *    `AppLayout` (agente C — `max-w-screen-2xl`). No imponemos ancho aquí
 *    para que el rediseño funcione tanto antes como después de M2.16
 *    Agente C.
 *  - El bloque `kpis` viene del response del dashboard (`data.kpis`,
 *    opcional). Se pasa al HeroStatusBar; si el backend aún no lo expone,
 *    el hero se renderiza sin chips.
 *  - Cada card sigue gestionando su loading/error state internamente.
 */
export function Dashboard(): ReactElement {
  const [weekStartISO, setWeekStartISO] = useState<string | undefined>(undefined);
  const dashboardQuery = useDashboard(weekStartISO);
  const markMutation = useMarkWorkout();
  const syncMutation = useStravaSync();

  function handleMark(id: number, status: WorkoutStatus) {
    markMutation.mutate({ id, status });
  }

  return (
    <section aria-labelledby="dashboard-title" className="space-y-6">
      <AlertsBanner />

      {/* M2.32.A — biometría matutina con form rápido + chip de readiness. */}
      <MorningBiometricsCard />

      <HeroStatusBar
        profileName={dashboardQuery.data?.profileName ?? ''}
        kpis={extractKpis(dashboardQuery.data)}
        lastSyncAt={dashboardQuery.data?.strava.lastSyncAt ?? null}
        stravaConnected={dashboardQuery.data?.strava.connected ?? false}
      />

      {dashboardQuery.isLoading && (
        <p role="status" aria-live="polite" className="text-slate-700">
          Cargando dashboard…
        </p>
      )}

      {dashboardQuery.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el dashboard. {dashboardQuery.error?.message ?? ''}
        </p>
      )}

      {dashboardQuery.data && (
        <DashboardBody
          data={dashboardQuery.data}
          onWeekChange={setWeekStartISO}
          onMark={handleMark}
          onSyncStrava={() => syncMutation.mutate(undefined)}
          syncPending={syncMutation.isPending}
          syncedCount={syncMutation.data?.syncedCount ?? null}
        />
      )}
    </section>
  );
}

/**
 * Lee el bloque `kpis` del response del dashboard. En `lib/workouts.ts` lo
 * declaramos como `z.unknown().optional()` para no acoplarlo al schema
 * detallado de `lib/dashboard.ts` (evita dependencia circular). Aquí lo
 * validamos con `DashboardKpisSchema.safeParse`. Si no llega o llega
 * malformado, devolvemos null y los chips no se pintan.
 */
function extractKpis(data: DashboardResponse | undefined): DashboardKpis | null {
  if (!data?.kpis) return null;
  const parsed = DashboardKpisSchema.safeParse(data.kpis);
  return parsed.success ? parsed.data : null;
}

// ---------------------------------------------------------------------------
// Body — todo el contenido condicional cuando hay data
// ---------------------------------------------------------------------------

function DashboardBody({
  data,
  onWeekChange,
  onMark,
  onSyncStrava,
  syncPending,
  syncedCount,
}: {
  data: DashboardResponse;
  onWeekChange: (iso: string) => void;
  onMark: (id: number, status: WorkoutStatus) => void;
  onSyncStrava: () => void;
  syncPending: boolean;
  syncedCount: number | null;
}): ReactElement {
  const isEmpty = data.week.totals.plannedCount === 0;
  // M2.17: selección de día en el heatmap. Sólo se setea desde celdas con
  // >1 actividad (multi). Las celdas con 1 actividad navegan directo a
  // `/entreno/$id` y NO actualizan este estado.
  const [selectedDay, setSelectedDay] = useState<ActivityHeatmapDay | null>(null);
  return (
    <>
      {/* Fila bento: FormFitness (8) + NextWorkout + Strava (4) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <FormFitnessChart weeks={12} />
        </div>
        <div className="flex flex-col gap-6 lg:col-span-4">
          <NextWorkoutCard nextWorkout={data.nextWorkout} onMark={onMark} />
          <StravaMiniCard
            strava={data.strava}
            onSync={onSyncStrava}
            pending={syncPending}
            syncedCount={syncedCount}
          />
        </div>
      </div>

      {/* Horas por disciplina (estrella visual del centro) */}
      <DisciplineHoursChart />

      {/* WeekSummary (6) + DisciplineStats (6) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <WeekSummaryCard totals={data.week.totals} days={data.week.days} />
        </div>
        <div className="lg:col-span-6">
          <DisciplineStatsGrid days={30} />
        </div>
      </div>

      {/* M2.17 — Heatmap clicable (8 col) + Panel detalle día (4 col) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <ActivityHeatmap days={182} onDaySelect={setSelectedDay} />
        </div>
        <div className="lg:col-span-4">
          <DaySelectionPanel
            day={selectedDay}
            onClose={() => setSelectedDay(null)}
          />
        </div>
      </div>

      {/* Navegación + vista semanal */}
      <WeekNavigator
        weekStartISO={data.week.weekStartISO}
        weekEndISO={data.week.weekEndISO}
        onChange={onWeekChange}
      />

      {isEmpty ? (
        <EmptyWeekState weekStartISO={data.week.weekStartISO} />
      ) : (
        <WeekGrid days={data.week.days} onMark={onMark} />
      )}

      {/* Últimas actividades */}
      <RecentActivitiesCard limit={8} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Próximo entreno (compacto)
// ---------------------------------------------------------------------------

function NextWorkoutCard({
  nextWorkout,
  onMark,
}: {
  nextWorkout: PlannedWorkout | null;
  onMark: (id: number, status: WorkoutStatus) => void;
}): ReactElement {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Próximo entreno
      </h2>
      {nextWorkout ? (
        <div className="mt-2 space-y-2">
          <p className="text-sm font-medium text-slate-900">
            <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${disciplineBadgeClass(nextWorkout.discipline)}`}>
              {disciplineLabel(nextWorkout.discipline)}
            </span>{' '}
            <span>{Math.round(nextWorkout.durationPlannedMin)} min</span>
          </p>
          <p className="text-xs text-slate-600">
            {formatDateTime(nextWorkout.datetime, nextWorkout.timezone)}
          </p>
          {nextWorkout.description && (
            <p className="line-clamp-2 text-xs text-slate-700">{nextWorkout.description}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onMark(nextWorkout.id, 'completed')}
              className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            >
              Marcar como hecho
            </button>
            <Link
              to="/entreno/$id"
              params={{ id: String(nextWorkout.id) }}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            >
              Ver detalle
            </Link>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-slate-600">Sin próximos entrenos.</p>
      )}
    </article>
  );
}

function StravaMiniCard({
  strava,
  onSync,
  pending,
  syncedCount,
}: {
  strava: DashboardStrava;
  onSync: () => void;
  pending: boolean;
  syncedCount: number | null;
}): ReactElement {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Strava</h2>
      {strava.connected ? (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-slate-700">
            Sincronizado {formatLastSync(strava.lastSyncAt ?? null)}.
          </p>
          {syncedCount !== null && (
            <p role="status" aria-live="polite" className="text-xs text-emerald-700">
              {syncedCount} actividad{syncedCount === 1 ? '' : 'es'} en el último sync.
            </p>
          )}
          <button
            type="button"
            onClick={onSync}
            disabled={pending}
            aria-busy={pending}
            className="inline-flex items-center gap-1 rounded-md bg-teal-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {pending ? 'Sincronizando…' : 'Sincronizar'}
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-slate-700">Conecta tu cuenta de Strava.</p>
          <Link
            to="/ajustes/strava"
            className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-600 focus:ring-offset-1"
          >
            Conectar Strava
          </Link>
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyWeekState({ weekStartISO: _weekStartISO }: { weekStartISO: string }): ReactElement {
  return (
    <section
      aria-label="Semana vacía"
      className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
    >
      <p className="text-sm text-slate-700">
        Aún no hay entrenamientos planificados para esta semana.
      </p>
      <Link
        to="/coach/nuevo-plan"
        className="mt-4 inline-flex items-center gap-1 rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
      >
        Generar plan semanal
      </Link>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Grid semanal
// ---------------------------------------------------------------------------

function WeekGrid({
  days,
  onMark,
}: {
  days: WeekDay[];
  onMark: (id: number, status: WorkoutStatus) => void;
}): ReactElement {
  return (
    <section aria-label="Vista semanal" className="rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-sm">
      <ul
        role="list"
        className="grid grid-cols-1 gap-3 lg:grid-cols-7"
      >
        {days.map((day) => (
          <DayColumn key={day.dateISO} day={day} onMark={onMark} />
        ))}
      </ul>
    </section>
  );
}

function DayColumn({
  day,
  onMark,
}: {
  day: WeekDay;
  onMark: (id: number, status: WorkoutStatus) => void;
}): ReactElement {
  const sorted = useMemo(
    () => [...day.workouts].sort((a, b) => Date.parse(a.datetime) - Date.parse(b.datetime)),
    [day.workouts],
  );
  const unplanned = day.unplannedActuals ?? [];
  const totalItems = sorted.length + unplanned.length;
  return (
    <li className="flex flex-col">
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          {dayShortLabel(day.dayKey)}
        </h3>
        <span className="text-xs text-slate-500">{formatDayNumber(day.dateISO)}</span>
      </header>
      <div className="flex flex-1 flex-col gap-2">
        {totalItems === 0 ? (
          <p className="rounded-md border border-dashed border-slate-200 px-2 py-3 text-center text-xs text-slate-400">
            —
          </p>
        ) : (
          <>
            {sorted.map((w) => <WorkoutCard key={`p${w.id}`} workout={w} onMark={onMark} />)}
            {unplanned.map((a) => <UnplannedActualCard key={`a${a.id}`} actual={a} />)}
          </>
        )}
      </div>
    </li>
  );
}

/**
 * Card compacta para un actual SIN planned (M2.8). Sin botones de marcado.
 */
function UnplannedActualCard({
  actual,
}: {
  actual: NonNullable<WeekDay['unplannedActuals']>[number];
}): ReactElement {
  const time = new Date(actual.datetime).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: actual.timezone,
  });
  return (
    <Link
      to="/entreno/$id"
      params={{ id: String(actual.id) }}
      className="block rounded-md border-l-4 border-purple-400 bg-white p-2 text-xs shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600"
      aria-label={`Entrenamiento no planificado de ${actual.discipline}, ${actual.durationMin} minutos`}
    >
      <div className="flex items-baseline justify-between">
        <span className="font-medium text-slate-900">{disciplineEmoji(actual.discipline)} {time}</span>
        <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-purple-800">
          Extra
        </span>
      </div>
      <p className="mt-1 text-slate-600">
        {actual.durationMin} min{actual.distanceM ? ` · ${(actual.distanceM / 1000).toFixed(1)} km` : ''}
      </p>
    </Link>
  );
}

function disciplineEmoji(d: string): string {
  switch (d) {
    case 'swim': return '🏊';
    case 'bike': return '🚴';
    case 'run': return '🏃';
    case 'strength': return '💪';
    case 'brick': return '🔁';
    default: return '⚡';
  }
}

// ---------------------------------------------------------------------------
// Helpers de display
// ---------------------------------------------------------------------------

function formatDateTime(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function dayShortLabel(dk: WeekDay['dayKey']): string {
  switch (dk) {
    case 'mon': return 'Lun';
    case 'tue': return 'Mar';
    case 'wed': return 'Mié';
    case 'thu': return 'Jue';
    case 'fri': return 'Vie';
    case 'sat': return 'Sáb';
    case 'sun': return 'Dom';
  }
}

function formatDayNumber(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      timeZone: 'UTC',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function formatLastSync(iso: string | null): string {
  if (!iso) return 'nunca';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'hace unos segundos';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 30) return `hace ${diffDay} d`;
  return new Date(iso).toLocaleDateString('es-ES');
}

// Re-export para tests que quieran reutilizarlos.
export {
  NextWorkoutCard as _NextWorkoutCard,
  StravaMiniCard as _StravaMiniCard,
};
